// frontend/src/services/reports.service.ts
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  getDoc,
  doc
} from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';

export interface OvertimeReport {
  id: string;
  projectName: string;
  projectCode: string;
  requesterId: string;
  requesterName: string;
  approverEmpId: string;
  approverName: string;
  status: 'pending' | 'approved' | 'rejected';
  startTime: Date;
  endTime: Date;
  totalHours: number;
  totalEmployees: number;
  employeeIds: string[];
  employeeDetails: EmployeeDetail[];
  requestTime: Date;
  approvalTime?: Date;
  remarks?: string;
  approvedBy?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

export interface EmployeeDetail {
  id: string;
  name: string;
  designation: string;
  department: string;
  employeeNumber: string;
  pin?: string;
  // Attendance fields (will be populated later)
  checkInTime?: string;
  checkOutTime?: string;
  actualHours?: number;
  attendanceStatus?: 'present' | 'absent' | 'partial';
}

export interface ReportFilters {
  status?: 'all' | 'pending' | 'approved' | 'rejected';
  dateFrom?: Date;
  dateTo?: Date;
  projectName?: string;
  requesterId?: string;
}

export interface EmployeeReportData extends EmployeeDetail {
  projectName: string;
  projectCode: string;
  requestId: string;
  overtimeDate: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  scheduledHours: number;
  status: string;
  requesterName: string;
  approverName: string;
  approvalDate?: string;
  remarks?: string;
}

class ReportsService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutes for reports

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Get all overtime reports for a specific requester
   */
  async getOvertimeReports(requesterId: string, filters?: ReportFilters): Promise<OvertimeReport[]> {
    try {
      console.log('Fetching overtime reports for requester:', requesterId);
      
      const cacheKey = `reports_${requesterId}_${JSON.stringify(filters || {})}`;
      const cached = this.getFromCache<OvertimeReport[]>(cacheKey);
      if (cached) return cached;

      // Build query
      let baseQuery = query(
        collection(db, 'overtime_requests'),
        where('requesterId', '==', requesterId),
        orderBy('requestTime', 'desc')
      );

      // Execute query
      const snapshot = await getDocs(baseQuery);
      const reports: OvertimeReport[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        
        // Convert Firestore data to report format
        const report: OvertimeReport = {
          id: docSnap.id,
          projectName: data.projectName || '',
          projectCode: data.projectCode || '',
          requesterId: data.requesterId || '',
          requesterName: data.requesterName || '',
          approverEmpId: data.approverEmpId || '',
          approverName: data.approverName || '',
          status: data.status || 'pending',
          startTime: data.startTime?.toDate() || new Date(),
          endTime: data.endTime?.toDate() || new Date(),
          totalHours: data.totalHours || 0,
          totalEmployees: data.totalEmployees || 0,
          employeeIds: data.employeeIds || [],
          employeeDetails: data.employeeDetails || [],
          requestTime: data.requestTime?.toDate() || new Date(),
          approvalTime: data.approvalTime?.toDate(),
          remarks: data.remarks || '',
          approvedBy: data.approvedBy || '',
          rejectedBy: data.rejectedBy || '',
          rejectionReason: data.rejectionReason || ''
        };

        // Apply filters
        if (this.matchesFilters(report, filters)) {
          reports.push(report);
        }
      }

      this.setCache(cacheKey, reports);
      console.log(`Found ${reports.length} overtime reports`);
      
      return reports;
    } catch (error) {
      console.error('Error fetching overtime reports:', error);
      throw new Error(`Failed to fetch reports: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get detailed employee report for a specific overtime request
   */
  async getEmployeeReport(requestId: string): Promise<EmployeeReportData[]> {
    try {
      console.log('Fetching employee report for request:', requestId);
      
      const cacheKey = `employee_report_${requestId}`;
      const cached = this.getFromCache<EmployeeReportData[]>(cacheKey);
      if (cached) return cached;

      // Get the overtime request
      const requestDoc = await getDoc(doc(db, 'overtime_requests', requestId));
      if (!requestDoc.exists()) {
        throw new Error('Overtime request not found');
      }

      const requestData = requestDoc.data();
      const employeeReports: EmployeeReportData[] = [];

      // Process each employee
      for (const empDetail of requestData.employeeDetails || []) {
        const employeeReport: EmployeeReportData = {
          ...empDetail,
          projectName: requestData.projectName || '',
          projectCode: requestData.projectCode || '',
          requestId: requestId,
          overtimeDate: requestData.startTime?.toDate().toLocaleDateString() || '',
          scheduledStartTime: requestData.startTime?.toDate().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }) || '',
          scheduledEndTime: requestData.endTime?.toDate().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }) || '',
          scheduledHours: requestData.totalHours || 0,
          status: requestData.status || 'pending',
          requesterName: requestData.requesterName || '',
          approverName: requestData.approverName || '',
          approvalDate: requestData.approvalTime?.toDate().toLocaleDateString() || '',
          remarks: requestData.remarks || '',
          // Attendance fields (placeholder for future integration)
          checkInTime: '', // Will be populated from attendance system
          checkOutTime: '', // Will be populated from attendance system
          actualHours: 0, // Will be calculated from attendance
          attendanceStatus: 'present' // Will be determined from attendance
        };

        employeeReports.push(employeeReport);
      }

      this.setCache(cacheKey, employeeReports);
      console.log(`Generated employee report with ${employeeReports.length} records`);
      
      return employeeReports;
    } catch (error) {
      console.error('Error generating employee report:', error);
      throw new Error(`Failed to generate employee report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Export overtime reports to Excel
   */
  async exportOvertimeReports(requesterId: string, filters?: ReportFilters): Promise<void> {
    try {
      console.log('Exporting overtime reports to Excel...');
      
      const reports = await this.getOvertimeReports(requesterId, filters);
      
      if (reports.length === 0) {
        throw new Error('No reports found to export');
      }

      // Prepare data for Excel
      const excelData = reports.map(report => ({
        'Request ID': report.id.slice(0, 8) + '...',
        'Project Name': report.projectName,
        'Project Code': report.projectCode,
        'Request Date': report.requestTime.toLocaleDateString(),
        'Overtime Date': report.startTime.toLocaleDateString(),
        'Start Time': report.startTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        'End Time': report.endTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        'Total Hours': report.totalHours,
        'Total Employees': report.totalEmployees,
        'Status': report.status.toUpperCase(),
        'Requester': report.requesterName,
        'Approver': report.approverName,
        'Approval Date': report.approvalTime?.toLocaleDateString() || '',
        'Remarks': report.remarks || ''
      }));

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Style the worksheet
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      
      // Set column widths
      worksheet['!cols'] = [
        { width: 15 }, // Request ID
        { width: 25 }, // Project Name
        { width: 15 }, // Project Code
        { width: 12 }, // Request Date
        { width: 12 }, // Overtime Date
        { width: 10 }, // Start Time
        { width: 10 }, // End Time
        { width: 10 }, // Total Hours
        { width: 12 }, // Total Employees
        { width: 10 }, // Status
        { width: 20 }, // Requester
        { width: 20 }, // Approver
        { width: 12 }, // Approval Date
        { width: 30 }  // Remarks
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Overtime Reports');

      // Generate filename
      const now = new Date();
      const filename = `Overtime_Reports_${now.getFullYear()}-${(now.getMonth() + 1)
        .toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      
      console.log(`Excel file exported: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw new Error(`Failed to export to Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Export employee report to Excel
   */
  async exportEmployeeReport(requestId: string, projectName: string): Promise<void> {
    try {
      console.log('Exporting employee report to Excel...');
      
      const employeeData = await this.getEmployeeReport(requestId);
      
      if (employeeData.length === 0) {
        throw new Error('No employee data found to export');
      }

      // Prepare data for Excel
      const excelData = employeeData.map(emp => ({
        'Employee ID': emp.id,
        'Employee Name': emp.name,
        'PIN/AUX': emp.pin || emp.employeeNumber,
        'Designation': emp.designation,
        'Department': emp.department,
        'Project Name': emp.projectName,
        'Project Code': emp.projectCode,
        'Overtime Date': emp.overtimeDate,
        'Scheduled Start': emp.scheduledStartTime,
        'Scheduled End': emp.scheduledEndTime,
        'Scheduled Hours': emp.scheduledHours,
        'Check-In Time': emp.checkInTime || 'N/A',
        'Check-Out Time': emp.checkOutTime || 'N/A',
        'Actual Hours': emp.actualHours || 0,
        'Attendance Status': emp.attendanceStatus || 'N/A',
        'Request Status': emp.status.toUpperCase(),
        'Requester': emp.requesterName,
        'Approver': emp.approverName,
        'Approval Date': emp.approvalDate || '',
        'Remarks': emp.remarks || ''
      }));

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Style the worksheet
      worksheet['!cols'] = [
        { width: 12 }, // Employee ID
        { width: 20 }, // Employee Name
        { width: 10 }, // PIN/AUX
        { width: 15 }, // Designation
        { width: 15 }, // Department
        { width: 25 }, // Project Name
        { width: 12 }, // Project Code
        { width: 12 }, // Overtime Date
        { width: 12 }, // Scheduled Start
        { width: 12 }, // Scheduled End
        { width: 12 }, // Scheduled Hours
        { width: 12 }, // Check-In Time
        { width: 12 }, // Check-Out Time
        { width: 12 }, // Actual Hours
        { width: 15 }, // Attendance Status
        { width: 12 }, // Request Status
        { width: 20 }, // Requester
        { width: 20 }, // Approver
        { width: 12 }, // Approval Date
        { width: 30 }  // Remarks
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Report');

      // Generate filename
      const now = new Date();
      const safeProjectName = projectName.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `Employee_Report_${safeProjectName}_${now.getFullYear()}-${(now.getMonth() + 1)
        .toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      
      console.log(`Employee report exported: ${filename}`);
    } catch (error) {
      console.error('Error exporting employee report:', error);
      throw new Error(`Failed to export employee report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get report statistics
   */
  async getReportStatistics(requesterId: string): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    totalEmployees: number;
    totalHours: number;
  }> {
    try {
      const reports = await this.getOvertimeReports(requesterId);
      
      const stats = {
        total: reports.length,
        pending: reports.filter(r => r.status === 'pending').length,
        approved: reports.filter(r => r.status === 'approved').length,
        rejected: reports.filter(r => r.status === 'rejected').length,
        totalEmployees: reports.reduce((sum, r) => sum + r.totalEmployees, 0),
        totalHours: reports.reduce((sum, r) => sum + r.totalHours, 0)
      };

      return stats;
    } catch (error) {
      console.error('Error getting report statistics:', error);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        totalEmployees: 0,
        totalHours: 0
      };
    }
  }

  /**
   * Check if report matches filters
   */
  private matchesFilters(report: OvertimeReport, filters?: ReportFilters): boolean {
    if (!filters) return true;

    if (filters.status && filters.status !== 'all' && report.status !== filters.status) {
      return false;
    }

    if (filters.dateFrom && report.requestTime < filters.dateFrom) {
      return false;
    }

    if (filters.dateTo && report.requestTime > filters.dateTo) {
      return false;
    }

    if (filters.projectName && !report.projectName.toLowerCase().includes(filters.projectName.toLowerCase())) {
      return false;
    }

    return true;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('Reports service cache cleared');
  }
}

export const reportsService = new ReportsService();